(() => {
  if (customElements.get('alicja-purchase-buttons')) return;

  class AlicjaPurchaseButtons extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.initialized = false;
      this.onReady = () => this.initialize();
      this.onVariant = event => {
        const variant = event.detail?.variant || null;
        if (variant && !this.variants.has(String(variant.id))) return;
        this.variant = variant;
        this.clearError();
        this.render();
      };
      this.onQuantity = event => {
        if (event.target.name === 'quantity' && event.target.form === this.form) {
          this.clearError();
          this.render();
        }
      };
      this.onBuy = () => this.buyNow();
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onReady, {once: true});
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.form = this.closest('form');
      this.root = this.closest('product-component') || this.closest('[data-product]') || this.parentElement;
      this.addButton = this.querySelector('[data-alicja-add-to-cart]');
      this.buyButton = this.querySelector('[data-alicja-buy-now]');
      this.buyText = this.querySelector('[data-alicja-buy-text]');
      this.error = this.querySelector('[data-alicja-buy-error]');
      const variants = this.querySelector('[data-alicja-purchase-variants]');
      if (!this.form || !this.addButton || !this.buyButton || !variants) return;
      try {
        this.variants = new Map(JSON.parse(variants.textContent).map(variant => [String(variant.id), variant]));
      } catch {
        return;
      }
      const id = new FormData(this.form).get('id');
      this.variant = this.variants.get(String(id)) || null;
      this.root.addEventListener('theme:variant:change', this.onVariant);
      this.root.addEventListener('input', this.onQuantity);
      this.root.addEventListener('change', this.onQuantity);
      this.buyButton.addEventListener('click', this.onBuy);
      this.initialized = true;
      this.render();
    }

    quantity() {
      const value = Number(new FormData(this.form).get('quantity') || 1);
      return Number.isInteger(value) && value > 0 ? value : 1;
    }

    money(cents) {
      if (typeof window.theme?.formatMoney === 'function') {
        const text = document.createElement('span');
        text.innerHTML = window.theme.formatMoney(cents, this.dataset.moneyFormat || window.theme.moneyFormat);
        return text.textContent;
      }
      return new Intl.NumberFormat('pl-PL', {style: 'currency', currency: this.dataset.currency || 'PLN'}).format(cents / 100);
    }

    render() {
      const available = Boolean(this.variant?.available);
      const text = this.addButton.querySelector('[data-add-to-cart-text]');
      if (text) {
        text.textContent = available
          ? `Dodaj do Koszyka - ${this.money(this.variant.price * this.quantity())}`
          : this.variant ? this.dataset.soldOutLabel || 'Wyprzedane' : 'Niedostępne';
      }
      this.buyButton.disabled = this.busy || !available;
      this.buyButton.setAttribute('aria-busy', String(this.busy));
      this.buyText.textContent = this.busy ? 'Przekierowywanie…' : 'Kup Teraz';
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    async buyNow() {
      if (this.busy || !this.variant?.available) return;
      this.clearError();
      if (!this.form.reportValidity()) return;
      const data = new FormData(this.form);
      const id = String(data.get('id') || '');
      const quantity = Number(data.get('quantity') || 1);
      if (!/^\d+$/.test(id) || String(this.variant.id) !== id || !Number.isInteger(quantity) || quantity < 1) {
        this.showError('Wybierz dostępny wariant i poprawną ilość produktu.');
        return;
      }
      const attributes = [];
      for (const [key, value] of data.entries()) {
        const property = key.match(/^properties\[(.+)\]$/);
        if (!property) continue;
        if (value instanceof File) {
          if (value.size) {
            this.showError('Aby przesłać plik z zamówieniem, dodaj produkt do koszyka.');
            return;
          }
        } else if (value !== '') {
          attributes.push({key: property[1], value: String(value)});
        }
      }
      const line = {merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity};
      if (attributes.length) line.attributes = attributes;
      const plan = String(data.get('selling_plan') || '');
      if (/^\d+$/.test(plan)) line.sellingPlanId = `gid://shopify/SellingPlan/${plan}`;
      this.busy = true;
      this.render();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        // Create a separate checkout. Never clear, replace or add to the existing Ajax cart.
        const response = await fetch('/api/2026-10/graphql.json', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          signal: controller.signal,
          body: JSON.stringify({
            query: 'mutation AlicjaBuyNow($input: CartInput!) { cartCreate(input: $input) { cart { checkoutUrl totalQuantity lines(first: 10) { nodes { quantity merchandise { ... on ProductVariant { id } } } } } userErrors { code message } } }',
            variables: {input: {buyerIdentity: {countryCode: this.dataset.country || 'PL'}, lines: [line]}},
          }),
        });
        if (!response.ok) throw new Error('checkout-unavailable');
        const result = await response.json();
        const payload = result.data?.cartCreate;
        if (result.errors?.length || !payload || payload.userErrors?.length) throw new Error('checkout-unavailable');
        const cart = payload.cart;
        const lines = cart?.lines?.nodes || [];
        if (lines.length !== 1 || lines[0].merchandise?.id !== line.merchandiseId || lines[0].quantity !== quantity || cart.totalQuantity !== quantity) {
          this.showError('Wybrana ilość produktu nie jest dostępna. Zmień ilość i spróbuj ponownie.');
          return;
        }
        const checkout = new URL(cart.checkoutUrl);
        const allowedHosts = [location.hostname, this.dataset.checkoutHost, this.dataset.shopHost].filter(Boolean);
        if (checkout.protocol !== 'https:' || !allowedHosts.includes(checkout.hostname)) throw new Error('checkout-unavailable');
        window.location.assign(checkout.href);
      } catch {
        this.showError('Nie udało się otworzyć kasy. Sprawdź dostępność wybranego wariantu i spróbuj ponownie.');
      } finally {
        window.clearTimeout(timeout);
        this.busy = false;
        this.render();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.root?.removeEventListener('theme:variant:change', this.onVariant);
      this.root?.removeEventListener('input', this.onQuantity);
      this.root?.removeEventListener('change', this.onQuantity);
      this.buyButton?.removeEventListener('click', this.onBuy);
      this.initialized = false;
    }
  }

  customElements.define('alicja-purchase-buttons', AlicjaPurchaseButtons);
})();
